<template>
  <div ref="userDropdown" class="sm:relative">
    <button
      @click="toggleMenu"
      @keydown="handleKeyDown"
      ref="userBtn"
      class="relative z-10 h-8 w-8 text-black rounded-full flex justify-center items-center transition-all hover:bg-primary-600"
      :class="[
        isOpen ? 'bg-primary-600' : 'bg-primary-400',
        !getUserAvatar && 'p-1'
      ]"
    >
      <!-- USER AVATAR -->
      <div :class="isLoaded ? 'w-full h-full relative' : 'invisible w-0 h-0'">
        <img
          v-if="getUserAvatar"
          loading="lazy"
          :src="getUserAvatar.image_url"
          @load="markLoaded"
          alt="User Avatar"
          :class="isLoaded && 'rounded-full w-full h-full'"
        >
        <div
          class="absolute top-0 left-0 rounded-full w-full h-full bg-black bg-opacity-0 hover:bg-opacity-20 focus:bg-opacity-20 active:bg-opacity-20"
          :class="isOpen && 'bg-opacity-10'"
        />
      </div>
      <!-- USER INITIALS IF NO AVATAR -->
      <p
        v-show="!getUserAvatar || !isLoaded"
        class="font-medium flex items-center justify-center text-white"
      >
        {{ userInitials }}
      </p>
      <!-- UNREAD MESSAGES BADGE -->
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 flex items-center justify-center h-4 min-w-[1rem] px-0.5 rounded-full bg-red-500 text-white text-[0.625rem] font-bold leading-none ring-2 ring-white dark:ring-navy-900 shadow"
      >
        {{ unreadCount }}
      </span>
    </button>
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-show="isOpen"
        class="absolute w-screen h-screen sm:w-auto sm:h-auto sm:right-0 z-10 mt-2 text-left sm:origin-top-right divide-y divide-gray-200 dark:divide-gray-600/70 lg:rounded-md bg-white dark:bg-navy-800 lg:bg-opacity-80 shadow-lg lg:backdrop-blur-xl dark:lg:border dark:border-white/10 ring-1 ring-black dark:ring-white/60 ring-opacity-5 dark:ring-opacity-100 focus:outline-none"
        :class="!screen.sm && 'left-0'"
        @keydown="handleMenuKeyDown"
      >
        <div class="px-4 py-4 sm:py-3">
          <p class="sm:text-sm">
            Signed in as
          </p>
          <p class="truncate sm:text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ getEmail }}
          </p>
        </div>
        <div class="py-1">
          <router-link
            @click="navToSitemap"
            to="/sitemap"
            ref="siteMapLink"
            tabindex="“-1”"
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon icon="fa-solid fa-location-dot" class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
            Change Site
          </router-link>
        </div>
        <div class="py-1">
          <router-link
            @click="closeMenu"
            to="/settings"
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon icon="fa-solid fa-gear" class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
            Settings
          </router-link>
          <router-link
            @click="closeMenu"
            to="/messages"
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <span class="relative mr-3 flex items-center">
              <font-awesome-icon icon="fa-solid fa-envelope" class="h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
              <span v-if="unreadCount > 0" class="absolute -top-1.5 -right-1.5 flex items-center justify-center h-3.5 min-w-[0.875rem] px-0.5 rounded-full bg-red-500 text-white text-[0.5625rem] font-bold leading-none">{{ unreadCount }}</span>
            </span>
            Messages
          </router-link>
          <router-link
            @click="closeMenu"
            to="/support"
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon icon="fa-solid fa-headset" class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
            Support
          </router-link>
          <router-link
            @click="navToPortal"
            to=""
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon icon="fa-solid fa-rectangle-history" class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
            Back To Portal
          </router-link>
        </div>
        <div class="py-1">
          <button
            @click="toggleTheme"
            class="group flex items-center w-full px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon
              :icon="isDark ? 'fa-regular fa-sun-bright' : 'fa-solid fa-moon'"
              class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300"
            />
            {{ isDark ? 'Light Mode' : 'Dark Mode' }}
          </button>
        </div>
        <div class="py-1">
          <router-link
            @click="logout"
            to=""
            class="group flex items-center px-4 py-3 sm:py-2 sm:text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-primary-15 dark:hover:bg-navy-400/20 focus:bg-primary-15 dark:focus:bg-navy-400/20 focus:outline-none active:bg-gray-100 active:text-gray-900 dark:active:text-gray-50"
          >
            <font-awesome-icon icon="fa-solid fa-arrow-right-from-bracket" class="mr-3 h-6 w-6 py-0.5 sm:h-4 sm:w-4 text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
            Sign out
          </router-link>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import { signOut } from 'aws-amplify/auth'
import Cookies from "js-cookie"
import sls from "@/store/storage"

export default {
  name: "UserDropdownComponent",
  emits: ["toggleMenu"],
  data() {
    return {
      isOpen: false,
      optionFocus: null,
      isLoaded: false
    }
  },
  computed: {
    ...mapState(["screen"]),
    ...mapGetters([
      "getEmail",
      "getFullName",
      "getUserAvatar",
      "getTheme",
      "getVisibleAnnouncements",
      "getChatUnreadTotal"
    ]),
    unreadCount() {
      return (this.getVisibleAnnouncements?.length || 0) + (this.getChatUnreadTotal || 0)
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    displayMap() {
      return window.innerWidth >= 1024
    },
    userInitials() {
      let substrings = this.getFullName.split(" ")
      substrings = substrings.filter(x => x.length > 0)
      let firstAndLast = substrings.length > 1 ? [substrings[0], substrings.pop()] : [substrings[0]]
      return firstAndLast.map(x => x[0].toUpperCase()).join("")
    },
    isDark() {
      return this.getTheme === 'dark'
    }
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick, true)
    document.addEventListener("keydown", this.handleEscape)
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick, true)
    document.removeEventListener("keydown", this.handleEscape)
  },
  methods: {
    ...mapMutations([
      "LogOutAT",
      "setActiveSite",
      "setSessionTimestamp",
      "setTheme"
    ]),
    closeMenu() {
      this.isOpen = false
      this.$emit("toggleMenu", false)
    },
    toggleMenu() {
      this.isOpen = !this.isOpen
      this.$emit("toggleMenu", this.isOpen)
    },
    toggleTheme() {
      this.setTheme(this.isDark ? 'light' : 'dark')
    },
    navToPortal() {
      this.setActiveSite(null);
      if (import.meta.env.VITE_DEV_NETWORK_ACCESS === "true") {
        this.logout()
      }
      else {
        Cookies.remove("cruzcontrolSite");
        sls.remove("cruzcontrolSite");
        location.href = "https://portal.cruzcontrol.app";
      }
    },
    navToSitemap() {
      Cookies.remove("cruzcontrolSite");
      sls.remove("cruzcontrolSite");
      this.setActiveSite(null);
      this.closeMenu()
    },
    handleKeyDown(e) {
      // focus on dropdown menu if key down
      if (e.keyCode === 40) {
        document.querySelector("a.group.flex").focus()
        this.optionFocus = 0
      }
    },
    handleMenuKeyDown(e) {
      let allOptions = document.querySelectorAll("a.group.flex")
      // go down on arrow down
      if (e.keyCode === 40) {
        if (this.optionFocus === allOptions.length - 1) return
        this.optionFocus++
        allOptions[this.optionFocus].focus()
      }
      // go up on arrow up
      if (e.keyCode === 38) {
        if (this.optionFocus === 0) this.$refs.userBtn.focus()
        else {
          this.optionFocus--
          allOptions[this.optionFocus].focus()
        }
      }
      // close menu on tab on last option
      if (e.code === "Tab" && this.optionFocus === allOptions.length - 1) {
        this.closeMenu()
        this.$refs.userBtn.focus()
      }
    },
    async logout() {
      await this.LogOutAT()
      this.setActiveSite(null);
      this.setSessionTimestamp(null)
      if (import.meta.env.VITE_DEV_NETWORK_ACCESS === "true") {
        try {
          signOut().then(() => {
            this.$router.push("/dev-login");
            Cookies.remove("cruzcontrol")
            Cookies.remove("cruzcontrolSite");
            sls.remove("cruzcontrolSite");
          });
        } catch (error) {
          console.error('error signing out: ', error);
        }
      }
      else {
        Cookies.remove("cruzcontrol")
        Cookies.remove("cruzcontrolSite");
        sls.remove("cruzcontrolSite");
        location.href = "https://portal.cruzcontrol.app/logout"
      }
    },
    async handleDocumentClick(e) {
      const clickedInsideDropdown = e.target === this.$refs.userDropdown || this.$refs.userDropdown.contains(e.target)
      if (!clickedInsideDropdown) {
        await this.$nextTick()
        if (this.isOpen) this.closeMenu()
      }
    },
    handleEscape(e) {
      if (e.key === "Esc" || e.key === "Escape") {
        this.closeMenu()
      }
    },
    markLoaded() {
      this.isLoaded = true
    }
  },
}
</script>

<style scoped>
/* ── Dark mode: avatar button ring ─────────────── */
/* :root[data-theme="dark"] button.rounded-full {
  background: rgba(0, 146, 191, 0.15) !important;
  border: 1px solid rgba(0, 188, 245, 0.20);
}
:root[data-theme="dark"] button.rounded-full:hover {
  background: rgba(0, 146, 191, 0.25) !important;
  border-color: rgba(0, 188, 245, 0.35);
} */

/* ── Dark mode: dropdown panel ─────────────────── */
/* :root[data-theme="dark"] .bg-white.shadow-lg,
:root[data-theme="dark"] .bg-white.ring-1 {
  background: rgba(15, 20, 35) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
} */
/* :root[data-theme="dark"] .text-gray-700 dark:text-gray-300 {
  color: rgba(255, 255, 255, 0.75) !important;
}
:root[data-theme="dark"] .text-gray-900 {
  color: rgba(255, 255, 255, 0.90) !important;
}
:root[data-theme="dark"] .text-gray-400 {
  color: rgba(255, 255, 255, 0.40) !important;
}
:root[data-theme="dark"] .hover\:bg-primary-15:hover,
:root[data-theme="dark"] .focus\:bg-primary-15:focus {
  background: rgba(0, 146, 191, 0.12) !important;
} */
</style>